import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { VisitorChatbotConfig, VisitorChatMessage, VisitorChatbotService as VisitorService } from '../../interfaces/visitor-chatbot.interface';
import { VisitorChatbotService } from '../../services/visitor-chatbot.service';
import { TextToSpeechService, VoiceOption } from '../../../core/services/text-to-speech.service';
import { SnackService } from '../snack/snack.service';
import { VoiceSettingsDialogComponent } from '../../../pages/chat-bot/voice-settings-dialog/voice-settings-dialog.component';
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";

@Component({
  selector: 'app-visitor-chatbot',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatInputModule,
    MatRadioModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatDialogModule,
    MatMenuModule,
    TranslateModule,
    MatRadioModule,
    MatSelectModule,

  ],
  templateUrl: './visitor-chatbot.component.html',
  styleUrls: ['./visitor-chatbot.component.scss']
})
export class VisitorChatbotComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly indexPageColors = {
    primaryColor: '#300458',
    headerColor: '#6a1b9a',
    buttonColor: '#e44891'
  };

  @Input() config!: VisitorChatbotConfig;
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('messageTextarea') messageTextarea!: ElementRef<HTMLTextAreaElement>;
  @Output() closeDialog = new EventEmitter<void>();
  @Output() minimize = new EventEmitter<void>();
  @Output() fullscreen = new EventEmitter<void>();

  messages: VisitorChatMessage[] = [];
  currentMessage = '';
  editingMessage: VisitorChatMessage | null = null; // Message currently being edited in the input box
  private userResizedInput = false; // true once the user manually drags the textarea size
  private lastAutoHeight = 0; // last height applied by the auto-resize logic
  isTyping = false;
  isMinimized = false;
  isFullscreen = false;
  isRTL = false;
  availableVoices: VoiceOption[] = [];
  selectedVoice?: SpeechSynthesisVoice;
  isVoiceDialogOpen = false;
  // Removed connectionStatus and isConnected since chatbot is always ready

  // Question limit properties
  questionCount = 0;
  maxQuestions = 7;
  isQuestionLimitReached = false;

  // Dynamic title based on source
  dynamicTitle = '';

  // Current chat source
  currentChatSource: 'support' | 'sanad' | 'hikma' = 'support';

  private subscriptions = new Subscription();
  private voiceDialogRef: MatDialogRef<VoiceSettingsDialogComponent> | null = null;

  private zoomLevel = 1;
  private resizeObserver?: ResizeObserver;
  private currentPath = window.location.pathname;
  private themeColors = {
    primaryColor: '#1976d2',
    headerColor: '#1976d2',
    buttonColor: '#1976d2'
  };
  recognizedText = '';
  selectedLang = 'en-US';
  isRecording = false;
  recognition: any;
  isVoiceDialogOpens = false;
  constructor(
    public chatbotService: VisitorChatbotService,
    private tts: TextToSpeechService,
    public translate: TranslateService,
    private snack: SnackService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  get appliedThemeColors() {
    if (this.isIndexPage) {
      return this.indexPageColors;
    }

    return this.themeColors;
  }

  get isIndexPage(): boolean {
    const normalizedPath = (this.currentPath || '/').toLowerCase().split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    return normalizedPath === '/' || /(^|\/)index(\/|$)/.test(normalizedPath);
  }

  cancelLanguageSelection() {
    this.isVoiceDialogOpens = false;
  }

  confirmLanguageSelection() {
    // أغلق الديلوج أولاً
    this.isVoiceDialogOpens = false;

    // ثم ابدأ التسجيل الصوتي إذا كان متاحًا
    if (this.recognition) {
      this.recognition.lang = this.selectedLang;
      this.recognition.start();
      this.isRecording = true; // ✅ تفعيل الكلاس بعد الضغط على زر البدء

      this.recognition.onend = () => {
        this.isRecording = false;
      };

      this.recognition.onerror = () => {
        this.isRecording = false;
      };
    }
  }



  // toggleVoiceSettings() {
  //   if (!this.recognition) {
  //     console.warn('SpeechRecognition is not supported.');
  //     return;
  //   }
  //
  //   if (!this.isVoiceDialogOpens && !this.showLangSelector) {
  //     // أول ضغطة: افتح اختيار اللغة
  //     this.showLangSelector = true;
  //     return;
  //   }
  //
  //   // لو تم اختيار اللغة وبدأ التسجيل
  //   if (!this.isVoiceDialogOpens && this.showLangSelector) {
  //     this.recognition.lang = this.selectedLang;
  //     this.recognition.start();
  //     this.isVoiceDialogOpens = true;
  //     this.showLangSelector = false;
  //     return;
  //   }
  //   // لو التسجيل شغال: وقّفه
  //   this.recognition.stop();
  //   this.isVoiceDialogOpens = false;
  // }



  toggleVoiceSettings() {
    if (!this.recognition) {
      console.warn('SpeechRecognition is not supported.');
      return;
    }

    if (!this.isVoiceDialogOpens) {
      // بدل فتح MatDialog، نظهر الديلوج الداخلي
      this.isVoiceDialogOpens = true;
      return;
    }

    // إذا مفتوح وأغلق، أوقف التسجيل
    this.recognition.stop();
    this.isVoiceDialogOpens = false;
    this.isRecording = false; // ✅ إيقاف الكلاس
  }




  // Simplified focus method for the simple textarea
  focusInput(): void {
    setTimeout(() => {
      if (this.messageTextarea) {
        const textarea = this.messageTextarea.nativeElement;
        textarea.focus();
        textarea.click();
        // Put cursor at end
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      } else {
        // Fallback to querySelector
        const textarea = document.querySelector('.visitor-chatbot-container .simple-textarea') as HTMLTextAreaElement;
        if (textarea) {
          textarea.focus();
          textarea.click();
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      }
    }, 50);
  }

  // Simplified debug methods for input interaction
  onInputSectionClick(event: Event): void {
    event.stopPropagation();
    this.focusInput();
  }

  // New method to focus textarea directly
  focusTextarea(event: Event): void {
    event.stopPropagation();
    setTimeout(() => {
      if (this.messageTextarea) {
        const textarea = this.messageTextarea.nativeElement;
        textarea.focus();
        textarea.click();
      }
    }, 10);
  }

  // Enhanced keydown handler
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      // Check if Hikma is disabled (not subscribed)
      if (this.isHikmaDisabled) {
        console.log('🚫 Hikma is disabled - keyboard submit blocked');
        this.snack.showSnack('AI_ASSISTANT.HIKMA_NOT_SUBSCRIBED_MESSAGE', 'warning');
        return;
      }

      this.sendMessage();
    }
  }

  // Click handler for send button with debugging
  onSendButtonClick(): void {
    console.log('🔥 Send button clicked from template!');

    // Check if Hikma is disabled (not subscribed)
    if (this.isHikmaDisabled) {
      console.log('🚫 Hikma is disabled - subscription required');
      this.snack.showSnack('AI_ASSISTANT.HIKMA_NOT_SUBSCRIBED_MESSAGE', 'warning');
      return;
    }

    // Block if daily conversation limit active or 7-question limit reached
    if (this.chatbotService.isLimitDayActive() || this.chatbotService.isQuestionLimitExceeded()) {
      this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.EXPERIMENTAL_ONE_CHAT_PER_DAY', 'warning');
      return;
    }

    this.sendMessage();
  }

  onInputFocus(): void {
    // Input focused
  }

  onInputBlur(): void {
    // Input lost focus
  }

  // Force enable method for simple textarea
  forceEnableInput(): void {
    setTimeout(() => {
      // Try ViewChild first
      if (this.messageTextarea) {
        const textarea = this.messageTextarea.nativeElement;
        textarea.disabled = false;
        textarea.readOnly = false;
        textarea.style.pointerEvents = 'auto';
        textarea.style.cursor = 'text';
        textarea.style.zIndex = '250';
        textarea.tabIndex = 0;
      }

      // Also query all textareas as fallback
      const textareas = document.querySelectorAll('.visitor-chatbot-container .simple-textarea');
      textareas.forEach((textarea: HTMLTextAreaElement) => {
        textarea.disabled = false;
        textarea.readOnly = false;
        textarea.style.pointerEvents = 'auto';
        textarea.style.cursor = 'text';
        textarea.style.zIndex = '250';
        textarea.tabIndex = 0;
      });
    }, 100);
  }

  // Simple auto-resize for textarea
  autoResizeTextarea(): void {
    if (this.messageTextarea) {
      const textarea = this.messageTextarea.nativeElement;

      // Respect a size the user has manually set via the resize grabber
      if (this.userResizedInput) {
        return;
      }
      // Detect a manual resize: the current height differs from the last height we set
      if (this.lastAutoHeight && Math.abs(textarea.offsetHeight - this.lastAutoHeight) > 2) {
        this.userResizedInput = true;
        return;
      }

      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 320) + 'px'; // Grow with content up to 320px
      this.lastAutoHeight = textarea.offsetHeight;
    }
  }

  // Drag the top-corner handle to make the input box bigger or smaller
  startInputResize(event: PointerEvent): void {
    event.preventDefault();
    if (!this.messageTextarea) {
      return;
    }
    const textarea = this.messageTextarea.nativeElement;
    const startY = event.clientY;
    const startHeight = textarea.offsetHeight;
    const minHeight = 52;
    const maxHeight = 320;

    const onMove = (e: PointerEvent) => {
      // Handle sits at the top, so dragging up enlarges the box
      const delta = startY - e.clientY;
      const newHeight = Math.min(Math.max(startHeight + delta, minHeight), maxHeight);
      textarea.style.height = newHeight + 'px';
    };
    const onUp = () => {
      // Lock in the manual size so auto-resize stops overriding it
      this.userResizedInput = true;
      this.lastAutoHeight = textarea.offsetHeight;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }

  // Enhanced sendMessage to include message status tracking
  sendMessage(): void {
    console.log('🔥 Send button clicked!');
    console.log('🔥 Current message:', this.currentMessage);
    console.log('🔥 Message trimmed:', this.currentMessage.trim());

    // Check if Hikma is disabled (not subscribed)
    if (this.isHikmaDisabled) {
      console.log('🚫 Hikma is disabled - subscription required');
      this.snack.showSnack('AI_ASSISTANT.HIKMA_NOT_SUBSCRIBED_MESSAGE', 'warning');
      return;
    }

    if (!this.currentMessage.trim()) {
      console.log('❌ Message is empty, not sending');
      return;
    }

    // If editing an existing message, apply the edit instead of adding a new one
    if (this.editingMessage) {
      const edited = this.editingMessage;
      const newText = this.currentMessage.trim();
      this.editingMessage = null;
      this.currentMessage = '';

      // Update the message text and drop everything that came after it
      this.chatbotService.applyEditAndTrim(edited.id, newText);

      // Re-process the edited message to get a fresh bot response
      this.processUserMessage(newText);

      setTimeout(() => {
        this.autoResizeTextarea();
        this.scrollToBottom();
      }, 10);
      return;
    }

    // Check if question limit is reached
    if (this.chatbotService.isQuestionLimitExceeded()) {
      console.log('🚫 Question limit reached, showing limit message');
      this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.EXPERIMENTAL_ONE_CHAT_PER_DAY', 'warning');
      this.currentMessage = '';
      return;
    }

    // Removed connection check - chatbot is always ready to send messages
    const messageText = this.currentMessage.trim();
    console.log('✅ Sending message:', messageText);

    // Create user message with sending status
    const userMessage = this.chatbotService.createMessageWithStatus(messageText, true, 'sending');
    this.chatbotService.addMessage(userMessage);

    // Update status to sent after a short delay
    setTimeout(() => {
      this.chatbotService.updateMessageStatus(userMessage.id, 'sent');
    }, 500);

    // Process the message (service handles adding bot response)
    this.processUserMessage(messageText);
    this.currentMessage = '';

    // Reset textarea height after clearing
    setTimeout(() => this.autoResizeTextarea(), 10);
  }

  ngOnInit(): void {
    if (!this.config) {
      console.error('VisitorChatbot: Configuration is required');
      return;
    }

    console.log('🚀 Initializing visitor chatbot...');

    this.isRTL = this.translate.currentLang === 'ar';
    this.currentPath = this.router.url.split('?')[0].split('#')[0] || '/';
    this.refreshThemeColors();
    this.loadVoices();
    this.subscribeToMessages();
    this.subscribeToChatSource(); // Subscribe to chat source for dynamic title
    this.setupZoomDetection(); // Add zoom detection
    this.updateQuestionCount(); // Initialize question count
    this.subscribeToRouteChanges();

    // Wait a moment for messages to be loaded from storage before initializing chat
    setTimeout(() => {
      // Delayed intro if enabled
      if (this.config.features.delayedIntro) {
        setTimeout(() => {
          this.initializeChat();
        }, 2000); // 2 second delay
      } else {
        this.initializeChat();
      }
    }, 100); // Small delay to ensure storage loading is complete

    // Force enable input after initialization
    setTimeout(() => {
      this.forceEnableInput();
    }, 500);

    console.log('✅ Visitor chatbot initialized successfully');

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (SpeechRecognition) {
      this.recognition = new SpeechRecognition();

      this.recognition.interimResults = false;
      this.recognition.maxAlternatives = 1;

      this.recognition.onresult = (event) => {
        this.currentMessage = event.results[0][0].transcript;
        console.log('Transcribed:', this.recognizedText);
      };

      this.recognition.onerror = (event) => {
        console.error('Speech error:', event.error);
        this.isVoiceDialogOpens = false;
      };

      this.recognition.onend = () => {
        this.isVoiceDialogOpens = false;
      };
    }

  }



  ngAfterViewInit(): void {
    // Initialize input properly
    this.initializeInput();
  }

  private initializeInput(): void {
    setTimeout(() => {
      this.forceEnableInput();

      // Test if textarea is working
      if (this.messageTextarea) {
        const textarea = this.messageTextarea.nativeElement;
        // Try to focus
        textarea.focus();
      }
    }, 500);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.voiceDialogRef) {
      this.voiceDialogRef.close();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Clean up body class when component is destroyed
    document.body.classList.remove('chatbot-fullscreen');
  }

  private loadVoices(): void {
    // Wait a bit for voices to load
    setTimeout(() => {
      this.availableVoices = this.tts.getAvailableVoices();
    }, 100);
  }

  // Helper method to create bot messages with icons
  private createBotMessageWithIcon(text: string, icon: string, type: 'text' | 'service' | 'contact' | 'link' | 'icon' = 'icon', metadata?: object): VisitorChatMessage {
    return this.chatbotService.createBotMessage(text, type, metadata, icon);
  }

  private initializeChat(): void {
    // Only add welcome message if there are no existing messages (fresh start)
    if (this.messages.length === 0) {
      const welcomeMessage = this.createBotMessageWithIcon(
        this.translate.instant(this.config.welcomeMessage),
        'waving_hand'
      );
      this.chatbotService.addMessage(welcomeMessage);
    }

    // Handle screen toolbar context - auto-submit page URL question
    if (this.isScreenToolbarContext) {
      setTimeout(() => {
        this.autoSubmitPageContextQuestion();
      }, 1000); // Small delay after welcome message
    }

    // Removed automatic intro and company info messages
    // Users can ask for more information if needed
  }

  /**
   * Auto-submit page context question for screen toolbar use case
   */
  private autoSubmitPageContextQuestion(): void {
    const currentUrl = window.location.pathname;
    const pageContext = this.getPageContextFromRoute(currentUrl);

    const contextQuestion = this.translate.instant('AI_ASSISTANT.SCREEN_TOOLBAR_AUTO_MESSAGE', {
      pageName: pageContext,
      url: currentUrl
    });

    // Create and add user message
    const userMessage = this.chatbotService.createUserMessage(contextQuestion);
    this.chatbotService.addMessage(userMessage);

    // Process the question
    this.processUserMessage(contextQuestion);
  }

  /**
   * Get page context from route for better user experience
   */
  private getPageContextFromRoute(route: string): string {
    // Extract meaningful page name from route
    const segments = route.split('/').filter(s => s.length > 0);
    if (segments.length === 0) return 'Home';

    // Get the last meaningful segment
    const lastSegment = segments[segments.length - 1];

    // Convert kebab-case to title case
    return lastSegment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private subscribeToMessages(): void {
    this.subscriptions.add(
      this.chatbotService.messages$.subscribe(messages => {
        this.messages = messages;
        setTimeout(() => this.scrollToBottom(), 100);
      })
    );

    this.subscriptions.add(
      this.chatbotService.isMinimized$.subscribe(minimized => {
        this.isMinimized = minimized;
      })
    );

    // Subscribe to typing indicator
    this.subscriptions.add(
      this.chatbotService.isTyping$.subscribe(typing => {
        this.isTyping = typing;
        if (typing) {
          setTimeout(() => this.scrollToBottom(), 100);
        }
      })
    );

    // Removed connection status subscription since chatbot is always ready
  }

  private subscribeToChatSource(): void {
    // Initialize with current source
    const currentSource = this.chatbotService.getChatSource();
    this.currentChatSource = currentSource;
    this.updateDynamicTitle(currentSource);

    // Subscribe to future changes
    this.subscriptions.add(
      this.chatbotService.chatSource$.subscribe(source => {
        this.currentChatSource = source;
        this.updateDynamicTitle(source);
      })
    );

    // Subscribe to language changes to update dynamic title
    this.subscriptions.add(
      this.translate.onLangChange.subscribe(() => {
        this.updateDynamicTitle(this.currentChatSource);
      })
    );
  }

  private subscribeToRouteChanges(): void {
    this.subscriptions.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.currentPath = event.urlAfterRedirects.split('?')[0].split('#')[0] || '/';
          this.refreshThemeColors();
        }
      })
    );
  }

  private refreshThemeColors(): void {
    if (typeof window === 'undefined') return;

    const styles = getComputedStyle(document.documentElement);
    const cssPrimary = styles.getPropertyValue('--primary-color')?.trim();

    // On non-index pages, prefer global theme color from CSS variables.
    const primary = cssPrimary || this.config?.theme?.primaryColor || '#1976d2';
    this.themeColors = {
      primaryColor: primary,
      headerColor: primary,
      buttonColor: primary
    };
  }

  private updateDynamicTitle(source: 'support' | 'sanad' | 'hikma'): void {
    switch (source) {
      case 'sanad':
        this.dynamicTitle = this.translate.instant('AI_ASSISTANT.SANAD_TITLE');
        break;
      case 'hikma':
        this.dynamicTitle = this.translate.instant('AI_ASSISTANT.HIKMA_TITLE');
        break;
      case 'support':
      default:
        this.dynamicTitle = this.translate.instant('AI_ASSISTANT.VISITORS_TITLE');
        break;
    }
  }

  /**
   * Check if Hikma service is disabled/not subscribed
   */
  get isHikmaDisabled(): boolean {
    // Only disable for Hikma source specifically
    return this.currentChatSource === 'hikma';
  }

  /**
   * Check if this is a screen toolbar context (should auto-submit page URL)
   */
  get isScreenToolbarContext(): boolean {
    // Screen toolbar context is when source is 'sanad' and we're not on support page
    // This will be handled by checking if we need to auto-submit page context
    return this.currentChatSource === 'sanad' && this.shouldAutoSubmitPageContext();
  }

  /**
   * Check if we should auto-submit page context (screen toolbar use case)
   */
  private shouldAutoSubmitPageContext(): boolean {
    // This could be determined by checking current route or other context
    // For now, we'll use the presence of page context as indicator
    const currentUrl = window.location.pathname;
    return !currentUrl.includes('/support') && this.currentChatSource === 'sanad';
  }

  /**
   * Get the appropriate placeholder text based on chat source and subscription status
   */
  get inputPlaceholder(): string {
    if (this.isHikmaDisabled) {
      return this.translate.instant('AI_ASSISTANT.HIKMA_NOT_SUBSCRIBED_MESSAGE');
    }

    if (this.chatbotService.areLimitsDisabled()) {
      return this.translate.instant(this.config.placeholder);
    }

    if (this.isQuestionLimitReached) {
      return this.translate.instant('VISITOR_CHATBOT.QUESTION_LIMIT_PLACEHOLDER');
    }

    return this.translate.instant(this.config.placeholder);
  }

  private updateQuestionCount(): void {
    this.questionCount = this.chatbotService.getCurrentQuestionCount();
    this.isQuestionLimitReached = this.chatbotService.isQuestionLimitExceeded();
    console.log(`📊 Updated question count: ${this.questionCount}/${this.maxQuestions}, limit reached: ${this.isQuestionLimitReached}`);
  }

  getRemainingQuestions(): number {
    return this.chatbotService.getRemainingQuestions();
  }

  private processUserMessage(message: string): void {
    console.log('🤔 Processing user message:', message);

    // Call the Bedrock API (service will handle typing indicator and bot response)
    this.chatbotService.askQuestion(message).subscribe({
      next: (response: string) => {
        // Aurora DB resuming special flag
        if (response === 'DB_RESUMING_AUTO_RETRY') {
          const friendlyMessage = this.translate.instant('VISITOR_CHATBOT.MESSAGES.DB_RESUMING');
          this.chatbotService.addMessage(this.chatbotService.createBotMessage(friendlyMessage, 'text'));

          setTimeout(() => {
            this.chatbotService.retryQuestionAfterDbResume(message).subscribe({
              next: (retryResponse: string) => {
                this.chatbotService.addMessage(this.chatbotService.createBotMessage(retryResponse, 'text'));
                this.updateQuestionCount();
                setTimeout(() => this.scrollToBottom(), 100);
              },
              error: () => {
                const fallback = this.chatbotService.createBotMessage(
                  this.translate.instant('VISITOR_CHATBOT.RESPONSES.DEFAULT'), 'text'
                );
                this.chatbotService.addMessage(fallback);
                this.updateQuestionCount();
                setTimeout(() => this.scrollToBottom(), 100);
              }
            });
          }, 10000);

          this.updateQuestionCount();
          setTimeout(() => this.scrollToBottom(), 100);
          return;
        }

        // Normal response handling
        const botMessage = this.chatbotService.createBotMessage(response, 'text');
        this.chatbotService.addMessage(botMessage);

        // Update question count in component
        this.updateQuestionCount();

        setTimeout(() => this.scrollToBottom(), 100);
      },
      error: (error) => {
        // If we receive a low-level error indicating DB resume (just in case)
        const errText = (error?.message || error || '').toString();
        if (errText.includes('AURORA_DB_RESUMING') || (
          errText.includes('Aurora DB instance') &&
          errText.includes('resuming after being auto-paused') &&
          errText.includes('Please wait a few seconds and try again')
        )) {
          const friendlyMessage = this.translate.instant('VISITOR_CHATBOT.MESSAGES.DB_RESUMING');
          this.chatbotService.addMessage(this.chatbotService.createBotMessage(friendlyMessage, 'text'));

          setTimeout(() => {
            this.chatbotService.retryQuestionAfterDbResume(message).subscribe((retryResponse: string) => {
              this.chatbotService.addMessage(this.chatbotService.createBotMessage(retryResponse, 'text'));
              this.updateQuestionCount();
              setTimeout(() => this.scrollToBottom(), 100);
            });
          }, 10000);

          this.updateQuestionCount();
          setTimeout(() => this.scrollToBottom(), 100);
          return;
        }

        // Fallback to local responses if API fails completely
        const fallbackResponse = this.getFallbackResponse(message);
        const botMessage = this.chatbotService.createBotMessage(fallbackResponse);
        this.chatbotService.addMessage(botMessage);

        // Update question count in component
        this.updateQuestionCount();

        setTimeout(() => this.scrollToBottom(), 100);
      }
    });
  }

  /**
   * Fallback responses when API is not available
   */
  private getFallbackResponse(message: string): string {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('pricing')) {
      return this.translate.instant('VISITOR_CHATBOT.RESPONSES.PRICING');
    } else if (lowerMessage.includes('demo') || lowerMessage.includes('trial')) {
      return this.translate.instant('VISITOR_CHATBOT.RESPONSES.DEMO');
    } else if (lowerMessage.includes('contact') || lowerMessage.includes('phone') || lowerMessage.includes('email')) {
      this.showContactInfo();
      return this.translate.instant('VISITOR_CHATBOT.RESPONSES.CONTACT');
    } else if (lowerMessage.includes('feature') || lowerMessage.includes('function')) {
      return this.translate.instant('VISITOR_CHATBOT.RESPONSES.FEATURES');
    } else if (lowerMessage.includes('support') || lowerMessage.includes('help')) {
      return this.translate.instant('VISITOR_CHATBOT.RESPONSES.SUPPORT');
    } else {
      return this.translate.instant('VISITOR_CHATBOT.RESPONSES.DEFAULT');
    }
  }

  selectService(service: VisitorService): void {
    // Add user message
    const userMessage = this.chatbotService.createUserMessage(
      this.translate.instant(service.label)
    );
    this.chatbotService.addMessage(userMessage);

    // Process service selection
    this.processServiceSelection(service);
  }

  private processServiceSelection(service: VisitorService): void {
    this.isTyping = true;

    this.chatbotService.simulateTyping().subscribe(() => {
      this.isTyping = false;
      let response = '';

      switch (service.id) {
        case 'general-info':
          response = this.translate.instant('VISITOR_CHATBOT.RESPONSES.GENERAL_INFO');
          break;
        case 'pricing':
          response = this.translate.instant('VISITOR_CHATBOT.RESPONSES.PRICING');
          break;
        case 'features':
          response = this.translate.instant('VISITOR_CHATBOT.RESPONSES.FEATURES');
          break;
        case 'demo':
          response = this.translate.instant('VISITOR_CHATBOT.RESPONSES.DEMO');
          break;
        case 'contact':
          response = this.translate.instant('VISITOR_CHATBOT.RESPONSES.CONTACT');
          this.showContactInfo();
          break;
        case 'support':
          response = this.translate.instant('VISITOR_CHATBOT.RESPONSES.SUPPORT');
          break;
        default:
          response = this.translate.instant('VISITOR_CHATBOT.RESPONSES.DEFAULT');
      }

      const botMessage = this.chatbotService.createBotMessage(response, 'service', { serviceId: service.id });
      this.chatbotService.addMessage(botMessage);
    });
  }

  private showContactInfo(): void {
    if (this.config.features.showContactInfo && this.config.contactInfo) {
      setTimeout(() => {
        const contactMessage = this.chatbotService.createBotMessage(
          `📞 ${this.config.contactInfo!.phone}\n📧 ${this.config.contactInfo!.email}\n🌐 ${this.config.contactInfo!.website}\n⏰ ${this.translate.instant(this.config.contactInfo!.workingHours || '')}`,
          'contact'
        );
        this.chatbotService.addMessage(contactMessage);
      }, 500);
    }
  }

  togglePlayMessage(message: VisitorChatMessage): void {
    if (!this.config.features.textToSpeech) return;

    // If this message is already playing, stop it
    if (message.isPlaying) {
      message.isPlaying = false;
      this.tts.stop();
      return;
    }

    // Stop any other message that may still be flagged as playing
    this.messages.forEach(msg => (msg.isPlaying = false));

    message.isPlaying = true;

    try {
      // The callback is bound to THIS message, so natural end / cancellation
      // only clears this message's playing state.
      this.tts.speak(message.text, () => {
        message.isPlaying = false;
      });
    } catch {
      message.isPlaying = false;
      this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.PLAY_ERROR', 'error');
    }
  }

  copyMessage(text: string): void {
    if (!this.config.features.copyMessages) return;

    navigator.clipboard.writeText(text).then(() => {
      this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.COPIED', 'success');
    }).catch(() => {
      this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.COPY_ERROR', 'error');
    });
  }

  onMinimize(): void {
    this.minimize.emit();
    this.chatbotService.minimize();
  }

  onClose(): void {
    this.closeDialog.emit();
    this.chatbotService.hide();

    // Clean up fullscreen mode when closing
    document.body.classList.remove('chatbot-fullscreen');
    this.isFullscreen = false;
  }

  newChat(): void {
    try {
      // Prevent starting a new chat if user has already consumed their 7 questions today
      if (this.chatbotService.isLimitDayActive() || this.chatbotService.isQuestionLimitExceeded()) {
        this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.EXPERIMENTAL_ONE_CHAT_PER_DAY', 'warning');
        return;
      }

      console.log('🆕 Starting new chat...');

      // Save current conversation to history and start fresh
      this.chatbotService.startNewConversation();

      // Reset question count for new session
      this.chatbotService.resetQuestionCount();
      this.updateQuestionCount();

      // Reset local state
      this.messages = [];
      this.isTyping = false;
      this.editingMessage = null;
      this.userResizedInput = false;
      this.lastAutoHeight = 0;
      this.currentMessage = '';
      this.currentMessage = '';

      // Clear any current input
      if (this.messageTextarea) {
        this.messageTextarea.nativeElement.value = '';
        this.autoResizeTextarea();
      }

      // Initialize new chat with fresh welcome messages after a short delay
      setTimeout(() => {
        this.initializeChat();
        this.scrollToBottom();
      }, 200);

      // Show feedback to user
      this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.NEW_CHAT_STARTED', 'success');

      console.log('✅ New chat started successfully');
    } catch (error) {
      console.error('❌ Error starting new chat:', error);
      this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.NEW_CHAT_ERROR', 'error');
    }
  }

  /**
   * Get conversation history for debugging or future features
   */
  getConversationHistory(): VisitorChatMessage[][] {
    return this.chatbotService.getConversationHistory();
  }

  /**
   * Clear all conversation history
   */
  clearAllHistory(): void {
    this.chatbotService.clearConversationHistory();
    this.snack.showSnack('VISITOR_CHATBOT.MESSAGES.HISTORY_CLEARED', 'success');
  }

  onFullscreen(): void {
    this.fullscreen.emit();
    this.isFullscreen = !this.isFullscreen;

    // Manage body class for fullscreen mode
    const body = document.body;
    if (this.isFullscreen) {
      body.classList.add('chatbot-fullscreen');
    } else {
      body.classList.remove('chatbot-fullscreen');
    }

    // Force scroll to bottom after fullscreen toggle
    setTimeout(() => {
      this.scrollToBottom();
      // Ensure input is still functional after fullscreen toggle
      this.forceEnableInput();
    }, 100);
  }

  toggleVoiceSettingsDialog(): void {
    if (!this.config.features.voiceSettings) return;

    if (this.voiceDialogRef) {
      this.voiceDialogRef.close();
      this.voiceDialogRef = null;
      this.isVoiceDialogOpen = false;
      return;
    }

    this.loadVoices();

    this.isVoiceDialogOpen = true;
    this.voiceDialogRef = this.dialog.open(VoiceSettingsDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      autoFocus: true,
      data: {
        voices: this.availableVoices,
        selectedVoice: this.selectedVoice
      },
      panelClass: ['voice-settings-dialog-panel', this.isFullscreen ? 'fullscreen-dialog' : ''],
      backdropClass: this.isFullscreen ? 'fullscreen-dialog-backdrop' : '',
      hasBackdrop: true,
      disableClose: false
    });

    this.voiceDialogRef.afterClosed().subscribe(result => {
      this.voiceDialogRef = null;
      this.isVoiceDialogOpen = false;
      if (result) {
        this.selectedVoice = result;
        this.tts.setVoice(result);
        this.snack.showSnack('VISITOR_CHATBOT.VOICE_CHANGED', 'success');
      }
    });
  }

  private setupZoomDetection(): void {
    // Detect zoom level changes using various methods
    this.detectInitialZoom();

    // Listen for resize events that might indicate zoom changes
    window.addEventListener('resize', () => {
      this.detectZoomChange();
    });

    // Use ResizeObserver for more accurate detection
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.detectZoomChange();
      });

      // Observe the document body for size changes
      this.resizeObserver.observe(document.body);
    }

    // Listen for visual viewport changes (mobile zoom)
    if ('visualViewport' in window && window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        this.detectZoomChange();
      });
    }
  }

  private detectInitialZoom(): void {
    this.zoomLevel = this.calculateZoomLevel();
    this.applyZoomAdjustments();
  }

  private detectZoomChange(): void {
    const newZoomLevel = this.calculateZoomLevel();
    if (Math.abs(newZoomLevel - this.zoomLevel) > 0.1) {
      this.zoomLevel = newZoomLevel;
      this.applyZoomAdjustments();
    }
  }

  private calculateZoomLevel(): number {
    // IMPORTANT: do NOT use window.devicePixelRatio here. On mobile devices the
    // DPR is 2–3 by default (it reflects screen pixel density, NOT browser zoom),
    // which previously misclassified every phone as "zoomed in" and applied the
    // .zoom-high width/height overrides — breaking the responsive mobile layout
    // and leaving the messages area blank.

    // Use the visual viewport scale, which is the actual pinch/zoom factor (1 = no zoom).
    const vv = window.visualViewport;
    if (vv && vv.scale) {
      return vv.scale;
    }

    // Desktop fallback: estimate browser zoom from outer vs inner width.
    if (window.outerWidth && window.innerWidth) {
      const ratio = window.outerWidth / window.innerWidth;
      if (isFinite(ratio) && ratio > 0) {
        return ratio;
      }
    }

    // Default: no zoom
    return 1;
  }

  private applyZoomAdjustments(): void {
    // Apply CSS custom properties based on detected zoom level
    const container = document.querySelector('.visitor-chatbot-container') as HTMLElement;
    if (container) {
      // Keep component width stable across browser/device DPR and responsive emulation.
      // Only allow subtle typography/spacing scaling.
      const scaleFactor = 1;
      const fontScale = Math.max(0.9, Math.min(1.1, 1 / Math.sqrt(this.zoomLevel)));
      const spacingScale = Math.max(0.9, Math.min(1.05, 1 / this.zoomLevel));

      container.style.setProperty('--scale-factor', scaleFactor.toString());
      container.style.setProperty('--font-scale', fontScale.toString());
      container.style.setProperty('--spacing-scale', spacingScale.toString());

      // Add zoom-level class for additional CSS targeting
      container.classList.remove('zoom-high', 'zoom-normal', 'zoom-low');
      if (this.zoomLevel > 1.5) {
        container.classList.add('zoom-high');
      } else if (this.zoomLevel < 0.8) {
        container.classList.add('zoom-low');
      } else {
        container.classList.add('zoom-normal');
      }
    }
  }

  // Returns true if the provided message is the most recent user message
  isLastUserMessage(message: VisitorChatMessage): boolean {
    const lastUserIndex = this.getLastUserMessageIndex();
    return lastUserIndex !== -1 && this.messages[lastUserIndex]?.id === message.id;
  }

  // Find the index of the last user message in the array
  private getLastUserMessageIndex(): number {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].isUser) return i;
    }
    return -1;
  }

  // Edit the last user message: load its text into the input box for editing.
  // Non-destructive — the original message stays until the edit is confirmed (or cancelled).
  editUserMessage(message: VisitorChatMessage): void {
    if (!message.isUser) return;

    // Clear any previous edit highlight
    this.messages.forEach(m => (m.isEditing = false));

    this.editingMessage = message;
    message.isEditing = true;

    // Move text into the input for editing
    this.currentMessage = (message.text || '').toString();

    // Focus input for user convenience
    this.focusInput();
  }

  // Cancel the in-progress edit and restore the input box
  cancelEditMessage(): void {
    if (this.editingMessage) {
      this.editingMessage.isEditing = false;
    }
    this.editingMessage = null;
    this.currentMessage = '';
    setTimeout(() => this.autoResizeTextarea(), 10);
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    } catch (err) {
      console.warn('Could not scroll to bottom:', err);
    }
  }
}
